import type {
  BattleEndMsg,
  BattleLockedMsg,
  BattleRoundMsg,
  BattleView,
  ChallengeDeclinedMsg,
  ChallengeIncomingMsg,
  RoomView,
  WelcomeMsg,
} from '@shared/protocol/messages';
import { Emitter } from '../game/core/Emitter';
import { nextStanceCooldown } from '../game/combat/engine';
import type { MultiplayerClient } from './MultiplayerClient';

type SessionEvents = {
  room: RoomView | null;
  battleStart: BattleView;
  battleSync: BattleView;
  round: BattleRoundMsg;
  locked: BattleLockedMsg;
  end: BattleEndMsg;
  challenge: ChallengeIncomingMsg;
  declined: ChallengeDeclinedMsg;
};

/**
 * Client-side mirror of the server-owned room/battle. It only stores what the
 * server sent; it never computes results itself.
 */
export class RoomSession extends Emitter<SessionEvents> {
  room: RoomView | null = null;
  battle: BattleView | null = null;
  lastEnd: BattleEndMsg | null = null;
  playerId: string | null = null;

  constructor(mp: MultiplayerClient) {
    super();
    mp.on('welcome', (m: WelcomeMsg) => {
      this.playerId = m.player.id;
      if (!m.resumedRoom) {
        this.room = null;
        this.battle = null;
        this.emit('room', null);
      }
    });
    mp.on('room.state', (m) => {
      this.room = m.room;
      if (!m.room || m.room.status !== 'battle') {
        if (this.battle && !m.room) this.battle = null;
      }
      this.emit('room', m.room);
    });
    mp.on('challenge.incoming', (m) => this.emit('challenge', m));
    mp.on('challenge.declined', (m) => this.emit('declined', m));
    mp.on('battle.start', (m) => {
      this.battle = m.battle;
      this.lastEnd = null;
      this.emit('battleStart', m.battle);
    });
    mp.on('battle.sync', (m) => {
      this.battle = m.battle;
      this.emit('battleSync', m.battle);
    });
    mp.on('battle.locked', (m) => {
      if (this.battle && this.battle.id === m.battleId && this.battle.round === m.round) this.battle.locked[m.seat] = true;
      this.emit('locked', m);
    });
    mp.on('battle.round', (m) => {
      const b = this.battle;
      if (b && b.id === m.battleId) {
        b.log.push(m.result);
        b.hp = m.result.hp;
        b.combo = m.result.combo;
        b.round = m.result.round + 1;
        b.deadline = m.deadline;
        b.locked = [false, false];
        // Same rule as the server engine, from the stances it applied.
        const cd = b.stanceCooldown ?? [0, 0];
        const st = m.result.stances ?? ['none', 'none'];
        b.stanceCooldown = [nextStanceCooldown(cd[0], st[0]), nextStanceCooldown(cd[1], st[1])];
        if (m.result.ink) b.ink = m.result.ink;
        if (m.result.chain) b.chain = m.result.chain;
      }
      this.emit('round', m);
    });
    mp.on('battle.end', (m) => {
      this.lastEnd = m;
      this.emit('end', m);
    });
  }

  /** My seat in the current battle (0 or 1). */
  get mySeat(): 0 | 1 {
    return this.battle?.players[1]?.id === this.playerId ? 1 : 0;
  }
}
